/**
 * Shared utility for deriving IconTimer visual state from remaining/total seconds.
 * Used by both the student QuizTimer and the teacher results grid.
 */

export type TimerIconState =
  | 'hidden'
  | 'not-started'
  | 'paused'
  | 'ended-early'
  | 'active'
  | 'warning'
  | 'critical'
  | 'expired'
  | 'completed';

// Switch to warning state when remaining time drops to 25% of total duration
const TIMER_WARNING_THRESHOLD_FRACTION = 0.25;
// Switch to critical state when remaining time drops to 10% of total duration
const TIMER_CRITICAL_THRESHOLD_FRACTION = 0.1;

/**
 * Derives the IconTimer display state from how much time remains.
 * Does NOT handle the paused or not-started cases — callers handle those separately.
 */
export function deriveTimerIconState(
  remainingSeconds: number,
  totalSeconds: number
): TimerIconState {
  if (remainingSeconds <= 0) {
    return 'expired';
  }

  if (
    remainingSeconds <
    Math.min(totalSeconds * TIMER_CRITICAL_THRESHOLD_FRACTION, 60)
  ) {
    return 'critical';
  }

  if (remainingSeconds < totalSeconds * TIMER_WARNING_THRESHOLD_FRACTION) {
    return 'warning';
  }

  return 'active';
}
